import { css, keyframes } from "styled-components";

/**
 * Helper function to convert from px to rem (1rem = 16px by browser default).
 * @param value number representing pixels.
 * @param omitUnit flag representing whether "rem" units should be ignored.
 * @returns string containing rem equivalent of pixel value.
 */
export const rem = (value: number, omitUnit = false) => {
  return value === 0 ? "0" : `${value / 16}${omitUnit === true ? "" : "rem"}`;
};

export const alpha = (color: string, opacity = 1) =>
  `color-mix(in srgb, ${color} ${opacity * 100}%, rgb(0,0,0,0))`;

export const bp = {
  medium: `${rem(1024, true)}em`, // large tablets/small laptops
  large1: `${rem(1200, true)}em`, // medium-sized laptops
  large2: `${rem(1440, true)}em`, // large-sized laptops
  large3: `${rem(1600, true)}em`,
  large4: `${rem(1920, true)}em`,
  extreme: `${rem(2100, true)}em`,
};

// Media queries
export const mq = {
  medium: `(min-width: ${bp.medium})`,
  large1: `(min-width: ${bp.large1})`,
  large2: `(min-width: ${bp.large2})`,
  large3: `(min-width: ${bp.large3})`,
  large4: `(min-width: ${bp.large4})`,
  extreme: `(min-width: ${bp.extreme})`,
};

/*********************************************************************
CSS UTILITIES (similar to Sass mixins)
**********************************************************************/
export const flexCenter = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const alignItemsCenter = css`
  display: flex;
  align-items: center;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const flexSpBetween = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const gridCenter = css`
  display: grid;
  place-items: center;
`;

export const absoluteCenter = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const pseudo = css`
  content: "";
  position: absolute;
`;

export const size = (width: string, height = width) => {
  return css`
    width: ${width};
    height: ${height};
  `;
};

export const circle = (diameter: string) => css`
  border-radius: 50%;
  ${size(diameter)}
`;

export const marginInlineAuto = (width: string) => css`
  margin-inline: auto;
  width: ${width};
`;

export const clipBorderRadius = (borderRadius: string) => css`
  border-radius: ${borderRadius};
  overflow: hidden;
`;

export const lineClamp = (numOfLines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${numOfLines};
  overflow: hidden;
  -webkit-box-orient: vertical;
`;

export const media = (breakpoint: string) =>
  `@media screen and (min-width: ${breakpoint})`;

export const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

export const overlayShow = keyframes`
    from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const fileRejectAnimation = keyframes`
    0% {
        transform: translateX(-3%);
    }
    50% {
        transform: translateX(3%);
    }
    100% {
        transform: translateX(0);
}
`;

export const fileAcceptAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
`;

export const spin = keyframes`
to {
  transform: rotate(360deg);
}
`;

export const slideDown = keyframes`
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  `;

export const slideUp = keyframes`
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  `;
